import React, { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  IconButton,
  Box,
  Button,
} from '@mui/material';
import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import { useAccelContext } from '../../contexts/AccelContext/AccelContext';

interface JournalEntry {
  id: string;
  date: string;
  transaction: string;
  account: string;
  debit: number;
  credit: number;
  details?: {
    account: string;
    debit?: number;
    credit?: number;
  }[];
}

const AccountingJournal: React.FC = () => {
  const { theme } = useAccelContext();
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  // Mock data - replace this with actual data from your API
  const journalEntries: JournalEntry[] = [
    {
      id: 'be9a8',
      date: 'May 10, 2024',
      transaction: 'Expense',
      account: '(2)',
      debit: 500.00,
      credit: 500.00,
    },
    {
      id: '851fa',
      date: 'May 10, 2024',
      transaction: 'Expense',
      account: '(2)',
      debit: 550.00,
      credit: 550.00,
    },
    {
      id: 'd1b5d',
      date: 'May 10, 2024',
      transaction: 'Invoice',
      account: '(2)',
      debit: 5000.00,
      credit: 5000.00,
      details: [
        { account: 'Service Revenue', credit: 5000.00 },
        { account: 'Accounts Receivable', debit: 5000.00 },
      ],
    },
    // Add more entries as needed
  ];

  const toggleRowExpansion = (id: string) => {
    setExpandedRows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5" component="h2" color={theme?.text?.hex}>
          Journal
        </Typography>
        <Button variant="outlined" color="primary">
          Add Entry
        </Button>
      </Box>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell></TableCell>
              <TableCell>Id</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Transaction</TableCell>
              <TableCell>Account</TableCell>
              <TableCell align="right">Debit</TableCell>
              <TableCell align="right">Credit</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {journalEntries.map((entry) => (
              <React.Fragment key={entry.id}>
                <TableRow>
                  <TableCell>
                    {entry.details && (
                      <IconButton
                        size="small"
                        onClick={() => toggleRowExpansion(entry.id)}
                      >
                        {expandedRows.has(entry.id) ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                      </IconButton>
                    )}
                  </TableCell>
                  <TableCell>{entry.id}</TableCell>
                  <TableCell>{entry.date}</TableCell>
                  <TableCell>{entry.transaction}</TableCell>
                  <TableCell>{entry.account}</TableCell>
                  <TableCell align="right">${entry.debit.toFixed(2)}</TableCell>
                  <TableCell align="right">${entry.credit.toFixed(2)}</TableCell>
                </TableRow>
                {expandedRows.has(entry.id) && entry.details && (
                  <>
                    {entry.details.map((detail, index) => (
                      <TableRow key={`${entry.id}-${index}`}>
                        <TableCell colSpan={2} />
                        <TableCell colSpan={2} />
                        <TableCell>{detail.account}</TableCell>
                        <TableCell align="right">
                          {detail.debit && `$${detail.debit.toFixed(2)}`}
                        </TableCell>
                        <TableCell align="right">
                          {detail.credit && `$${detail.credit.toFixed(2)}`}
                        </TableCell>
                      </TableRow>
                    ))}
                  </>
                )}
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default AccountingJournal;